/*
 * 会话同步（方案 A：登录一次，任意设备输门禁密码即可进对应已登录账号）。
 *
 * tweb 的整个会话 = localStorage 里的 `accountN`（AccountSessionData）：
 * 各 DC 的 auth_key / server_salt + userId + dcId 等。只要把这个对象写回
 * `accountN`，MTProto 就直接按已登录状态连接（见 apiManager.ts 读
 * AccountController.get(...)[`dc${dcId}_auth_key`]）。
 *
 * 流程：本机已登录 -> 用门禁密码派生密钥加密后上传服务器；本机未登录 ->
 * 从服务器下载密文，用门禁密码解密后写回本地。服务器只存密文，无法解密。
 *
 * 限制：这是「克隆会话」，同一会话同一时刻只能一台设备用（Telegram 的
 * 消息序号机制决定），两台设备同时开同一个账号会互相抢消息。
 */
import AccountController from '@lib/accounts/accountController';
import type {AccountSessionData, ActiveAccountNumber} from '@lib/accounts/types';
import rootScope from '@lib/rootScope';

const PBKDF2_ITERATIONS = 250000;

function toB64(bytes: Uint8Array): string {
  let s = '';
  for(let i = 0; i < bytes.length; i++) {
    s += String.fromCharCode(bytes[i]);
  }
  return btoa(s);
}

function fromB64(str: string): Uint8Array<ArrayBuffer> {
  const s = atob(str);
  const bytes = new Uint8Array(s.length);
  for(let i = 0; i < s.length; i++) {
    bytes[i] = s.charCodeAt(i);
  }
  return bytes;
}

async function deriveKey(password: string, salt: BufferSource): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256'},
    material,
    {name: 'AES-GCM', length: 256},
    false,
    ['encrypt', 'decrypt']
  );
}

async function encryptSession(data: AccountSessionData, password: string) {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(password, salt);
  const plaintext = new TextEncoder().encode(JSON.stringify(data));
  const ciphertext = new Uint8Array(await crypto.subtle.encrypt({name: 'AES-GCM', iv}, key, plaintext));

  return {
    salt: toB64(salt),
    iv: toB64(iv),
    ciphertext: toB64(ciphertext)
  };
}

async function decryptSession(blob: {salt: string, iv: string, ciphertext: string}, password: string): Promise<AccountSessionData> {
  const key = await deriveKey(password, fromB64(blob.salt));
  const plaintext = await crypto.subtle.decrypt(
    {name: 'AES-GCM', iv: fromB64(blob.iv)},
    key,
    fromB64(blob.ciphertext)
  );
  return JSON.parse(new TextDecoder().decode(plaintext)) as AccountSessionData;
}

/**
 * 门禁校验通过后调用（此时握有明文密码）。
 * @param account 门禁返回的账号号
 * @param password 用户输入的门禁密码（只在内存中，不落盘）
 * @param sessionFromServer /api/auth 附带返回的加密会话（可能为 null）
 */
export async function syncSessionForAccount(
  account: ActiveAccountNumber,
  password: string,
  sessionFromServer: {salt: string, iv: string, ciphertext: string} | null
) {
  const local = await AccountController.get(account);

  if(local.userId) {
    // 本机已登录 -> 加密上传，供其它设备下载（去掉 push 相关字段，让每台设备自建 push key）
    const exportable: AccountSessionData = {...local};
    delete exportable.push_key;
    delete exportable.push_key_fingerprint;

    try {
      const blob = await encryptSession(exportable, password);
      await fetch('/api/session', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({password, account, blob})
      });
    } catch(e) {
      // 上传失败不影响本机使用
    }
  } else if(sessionFromServer) {
    // 本机未登录 -> 解密服务器会话并写回本地
    try {
      const data = await decryptSession(sessionFromServer, password);
      if(data && data.userId) {
        await AccountController.update(account, data, true);
      }
    } catch(e) {
      // 解密失败（密码不匹配/密文损坏）→ 回落到正常登录流程
    }
  }
}

/**
 * 退出登录时清掉服务器上该账号的加密会话。
 *
 * 背景：tweb 退出登录只清本地会话，不清服务器上的加密会话 blob。若不清，
 * 下次进门禁时 syncSessionForAccount 会把那个已失效的会话下载解密导入，
 * 导致「死会话 -> 连不上 -> 自动退出 -> 重载 -> 门禁」的死循环，还挡住扫码登录。
 *
 * 用 sendBeacon：页面即将卸载/重载时也能把删除请求发出去（fire-and-forget）。
 */
export function installLogoutSessionCleanup() {
  rootScope.addEventListener('logging_out', ({accountNumber}) => {
    const accounts = accountNumber !== undefined ? [accountNumber] : [1, 2, 3];
    for(const account of accounts) {
      const body = new Blob([JSON.stringify({account})], {type: 'application/json'});
      if(navigator.sendBeacon) {
        navigator.sendBeacon('/api/session/delete', body);
      } else {
        fetch('/api/session/delete', {method: 'POST', body}).catch(() => {});
      }
    }
  });
}
